/**
 * Text formatting utilities for form fields
 */

/**
 * Capitalize first letter of each word (Title Case)
 * @param {string} str - Input string
 * @returns {string} - Title cased string
 */
export const toTitleCase = (str) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Capitalize first letter of sentence
 * @param {string} str - Input string
 * @returns {string} - Sentence cased string
 */
export const toSentenceCase = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Format name - Title Case with special handling
 * @param {string} name - Name to format
 * @returns {string} - Properly formatted name
 */
export const formatName = (name) => {
  if (!name) return '';
  // Handle special cases like "McDonald", "O'Brien"
  return name
    .toLowerCase()
    .split(' ')
    .map(word => {
      // Handle names with apostrophes (O'Brien, D'Souza)
      if (word.includes("'")) {
        return word.split("'").map(part => 
          part.charAt(0).toUpperCase() + part.slice(1)
        ).join("'");
      }
      // Handle names with "Mc" prefix (McDonald)
      if (word.startsWith('mc') && word.length > 2) {
        return 'Mc' + word.charAt(2).toUpperCase() + word.slice(3);
      }
      // Handle names with "Mac" prefix (MacArthur)
      if (word.startsWith('mac') && word.length > 3) {
        return 'Mac' + word.charAt(3).toUpperCase() + word.slice(4);
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

/**
 * Format address - Capitalize appropriately
 * @param {string} address - Address string
 * @returns {string} - Formatted address
 */
export const formatAddress = (address) => {
  if (!address) return '';
  // Capitalize first letter of each word, but keep common abbreviations
  const commonAbbreviations = ['apt', 'st', 'rd', 'ave', 'blvd', 'dr', 'ln', 'ct', 'pl', 'no', 'nr'];
  
  return address
    .split(' ')
    .map(word => {
      const lower = word.toLowerCase().replace(/[.,]/g, '');
      if (commonAbbreviations.includes(lower)) {
        return word.toUpperCase();
      }
      // Keep numbers as-is, capitalize words
      if (/^\d+$/.test(word)) {
        return word;
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

/**
 * Format company name
 * @param {string} company - Company name
 * @returns {string} - Formatted company name
 */
export const formatCompanyName = (company) => {
  if (!company) return '';
  // Common company suffixes to keep uppercase
  const suffixes = ['llp', 'llc', 'pvt', 'ltd', 'inc', 'corp', 'co', 'plc'];
  const techTerms = ['it', 'ai', 'ml', 'hr', 'crm', 'erp', 'saas', 'iot'];
  
  return company
    .split(' ')
    .map(word => {
      const lower = word.toLowerCase().replace(/[.,]/g, '');
      if (suffixes.includes(lower) || techTerms.includes(lower)) {
        return word.toUpperCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

/**
 * Format email - Always lowercase
 * @param {string} email - Email address
 * @returns {string} - Lowercase email
 */
export const formatEmail = (email) => {
  if (!email) return '';
  return email.toLowerCase().trim();
};

/**
 * Format PAN number - Always uppercase
 * @param {string} pan - PAN number
 * @returns {string} - Uppercase PAN
 */
export const formatPAN = (pan) => {
  if (!pan) return '';
  return pan.toUpperCase().replace(/[^A-Z0-9]/g, '');
};

/**
 * Format phone number - Clean and format
 * @param {string} phone - Phone number
 * @returns {string} - Cleaned phone number
 */
export const formatPhone = (phone) => {
  if (!phone) return '';
  // Remove all non-digit characters except + at start
  let cleaned = phone.replace(/[^\d+]/g, '');
  // Ensure only one + at start
  if (cleaned.startsWith('+')) {
    cleaned = '+' + cleaned.slice(1).replace(/\+/g, '');
  }
  return cleaned;
};

/**
 * Format pincode - Numbers only
 * @param {string} pincode - Pincode
 * @returns {string} - Cleaned pincode
 */
export const formatPincode = (pincode) => {
  if (!pincode) return '';
  return pincode.replace(/\D/g, '').slice(0, 6);
};

/**
 * Format degree/course name
 * @param {string} degree - Degree name
 * @returns {string} - Formatted degree
 */
export const formatDegree = (degree) => {
  if (!degree) return '';
  // Common abbreviations to keep uppercase
  const abbreviations = ['b.tech', 'btech', 'm.tech', 'mtech', 'b.e', 'be', 'm.e', 'me', 
    'bba', 'mba', 'bca', 'mca', 'b.sc', 'bsc', 'm.sc', 'msc', 'b.com', 'bcom', 
    'm.com', 'mcom', 'b.a', 'ba', 'm.a', 'ma', 'phd', 'ph.d', 'llb', 'llm'];
  
  return degree
    .split(' ')
    .map(word => {
      const lower = word.toLowerCase().replace(/[.,]/g, '');
      if (abbreviations.includes(lower)) {
        return word.toUpperCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

/**
 * Format date to DD/MM/YYYY
 * @param {string|Date} date - Date to format
 * @returns {string} - Formatted date string
 */
export const formatDateDDMMYYYY = (date) => {
  if (!date) return '-';
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return '-';
  }
};

/**
 * Format datetime to DD/MM/YYYY HH:MM
 * @param {string|Date} date - Date to format
 * @returns {string} - Formatted datetime string
 */
export const formatDateTimeDDMMYYYY = (date) => {
  if (!date) return '-';
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch {
    return '-';
  }
};

/**
 * Format date to DD-Mon-YYYY format (e.g., "07-Dec-2020")
 * @param {string|Date} dateStr - Date string in any format
 * @returns {string} - Date string in DD-Mon-YYYY format
 */
export const formatDateDDMonYYYY = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let d;
    
    // Handle YYYY-MM-DD format
    if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-');
      return `${day}-${months[parseInt(month, 10) - 1]}-${year}`;
    }
    
    // Handle DD-MM-YYYY format
    if (typeof dateStr === 'string' && /^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
      const [day, month, year] = dateStr.split('-');
      return `${day}-${months[parseInt(month, 10) - 1]}-${year}`;
    }
    
    // Try parsing as date
    d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr || '-';
    
    const day = String(d.getDate()).padStart(2, '0');
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return dateStr || '-';
  }
};

/**
 * Format date string from YYYY-MM-DD to DD-Mon-YYYY for display
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {string} - Date string in DD-Mon-YYYY format
 */
export const formatDateForDisplay = (dateStr) => {
  if (!dateStr) return '';
  return formatDateDDMonYYYY(dateStr);
};

/**
 * Legacy format date string (kept for backwards compatibility)
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {string} - Date string in DD-MM-YYYY format
 */
export const formatDateForDisplayLegacy = (dateStr) => {
  if (!dateStr) return '';
  try {
    // If already in DD-MM-YYYY format, return as is
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
      return dateStr;
    }
    // Convert from YYYY-MM-DD to DD-MM-YYYY
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-');
      return `${day}-${month}-${year}`;
    }
    // Try parsing as date
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return dateStr;
  }
};

/**
 * Format date input value - converts DD-MM-YYYY to YYYY-MM-DD for input
 * @param {string} dateStr - Date string in DD-MM-YYYY format
 * @returns {string} - Date string in YYYY-MM-DD format for input
 */
export const formatDateForInput = (dateStr) => {
  if (!dateStr) return '';
  try {
    // If already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    // Convert from DD-MM-YYYY to YYYY-MM-DD
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
      const [day, month, year] = dateStr.split('-');
      return `${year}-${month}-${day}`;
    }
    // Try parsing as date
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${year}-${month}-${day}`;
  } catch {
    return dateStr;
  }
};

/**
 * Validate email - must contain @
 * @param {string} email - Email to validate
 * @returns {boolean} - Is valid email
 */
export const isValidEmail = (email) => {
  if (!email) return false;
  // Basic email validation with @ required
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Format and validate phone number - max 10 digits
 * @param {string} phone - Phone number
 * @returns {string} - Cleaned phone number (max 10 digits)
 */
export const formatPhoneMax10 = (phone) => {
  if (!phone) return '';
  // Remove all non-digit characters
  const digitsOnly = phone.replace(/\D/g, '');
  // Return max 10 digits
  return digitsOnly.slice(0, 10);
};

/**
 * Validate phone number - must be exactly 10 digits
 * @param {string} phone - Phone to validate
 * @returns {boolean} - Is valid phone (10 digits)
 */
export const isValidPhone = (phone) => {
  if (!phone) return false;
  const digitsOnly = phone.replace(/\D/g, '');
  return digitsOnly.length === 10;
};

/**
 * Auto-format field based on field type
 * @param {string} value - Field value
 * @param {string} fieldType - Type of field
 * @returns {string} - Formatted value
 */
export const autoFormatField = (value, fieldType) => {
  if (!value) return '';
  
  switch (fieldType) {
    case 'name':
    case 'father_name':
    case 'mother_name':
    case 'reference_name':
      return formatName(value);
    
    case 'email':
      return formatEmail(value);
    
    case 'phone':
    case 'alternate_phone':
      return formatPhoneMax10(value);
    
    case 'company':
    case 'company_name':
      return formatCompanyName(value);
    
    case 'address':
    case 'complete_address':
      return formatAddress(value);
    
    case 'city':
    case 'state':
    case 'designation':
    case 'department':
      return toTitleCase(value);
    
    case 'pan':
    case 'pan_number':
      return formatPAN(value);
    
    case 'pincode':
      return formatPincode(value);
    
    case 'degree':
    case 'course':
    case 'qualification':
      return formatDegree(value);
    
    case 'institution':
    case 'university':
    case 'college':
      return toTitleCase(value);
    
    default:
      return value;
  }
};

export default {
  toTitleCase,
  toSentenceCase,
  formatName,
  formatAddress,
  formatCompanyName,
  formatEmail,
  formatPAN,
  formatPhone,
  formatPhoneMax10,
  formatPincode,
  formatDegree,
  formatDateDDMMYYYY,
  formatDateTimeDDMMYYYY,
  formatDateDDMonYYYY,
  formatDateForDisplay,
  formatDateForInput,
  isValidEmail,
  isValidPhone,
  autoFormatField
};
